'use client';

import { useCallback, useEffect, useState } from 'react';

import { useAppRouter, type IAppRouter } from '@/hooks/use-app-router';
import { completeFirebaseSocialLogin } from '@/lib/auth/complete-firebase-social-login';
import {
    type IFirebaseOAuthRedirectTracking,
    getOAuthReturnToForCurrentPage,
    sanitizeReturnToForReplace,
} from '@/lib/auth/firebase-oauth-session';
import { startFacebookOAuthRedirect } from '@/lib/facebook/oauth-redirect';
import {
    isFacebookSdkReady,
    loadFacebookSdk,
    loginWithFacebookForAccessToken,
    preloadFacebookSdk,
} from '@/lib/facebook/sdk';
import { signInWithFacebookAccessToken } from '@/lib/firebase/auth';
import { useToast } from '@/providers/root/toast';
import { readArmsFailDetail } from '@/utils/arms/common';
import { reportLoginAttempt, reportLoginFail } from '@/utils/arms/login-events';
import { isMobileBrowser } from '@/utils/device/is-mobile-browser';

const DEFAULT_AFTER_LOGIN = '/profile';

export interface IUseFacebookFirebaseLoginOptions {
    tracking: IFirebaseOAuthRedirectTracking;
    /** 登录成功后 replace 目标；默认当前页 */
    returnTo?: string;
    /** 弹窗等场景：SDK 登录成功后回调（如 closeLoginModal），不整页跳转 */
    onSuccess?: () => void;
}

async function finishFirebaseLoginWithAccessToken(
    accessToken: string,
    options: IUseFacebookFirebaseLoginOptions,
    addToast: (message: string) => void,
    router: IAppRouter
): Promise<boolean> {
    const userCredential = await signInWithFacebookAccessToken(accessToken);
    const idToken = await userCredential.user.getIdToken();

    const result = await completeFirebaseSocialLogin({
        idToken,
        tracking: options.tracking,
        onErrorToast: addToast,
    });

    if (!result.success) {
        return false;
    }

    if (options.onSuccess) {
        options.onSuccess();
        return true;
    }

    const to = sanitizeReturnToForReplace(options.returnTo ?? null, DEFAULT_AFTER_LOGIN);
    router.replace(to);
    return true;
}

export function useFacebookFirebaseLogin() {
    const router = useAppRouter();
    const { addToast } = useToast();
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        preloadFacebookSdk();
    }, []);

    const loginWithFacebook = useCallback(
        (options: IUseFacebookFirebaseLoginOptions) => {
            if (isLoading) {
                return;
            }

            const returnTo = options.returnTo ?? getOAuthReturnToForCurrentPage();

            // 移动端：整页 OAuth redirect，避免 FB.login 弹窗被拦截 / 无手势上下文
            if (isMobileBrowser()) {
                setIsLoading(true);
                const { login_method, trigger_scene } = options.tracking;
                if (login_method) {
                    reportLoginAttempt({ login_method, trigger_scene });
                }
                try {
                    startFacebookOAuthRedirect(returnTo, options.tracking);
                } catch (err) {
                    setIsLoading(false);
                    const message = (err as Error)?.message;
                    if (login_method) {
                        reportLoginFail({
                            login_method,
                            trigger_scene,
                            fail_reason: message?.includes('Facebook App ID is not configured')
                                ? 'facebook_app_id_not_configured'
                                : 'facebook_oauth_redirect_failed',
                            fail_detail: message,
                        });
                    }
                    if (message?.includes('Facebook App ID is not configured')) {
                        addToast('Facebook login is not configured');
                    } else {
                        addToast('Login failed');
                    }
                }
                return;
            }

            setIsLoading(true);

            const runSdkLogin = async () => {
                try {
                    if (!isFacebookSdkReady()) {
                        await loadFacebookSdk();
                    }
                    const accessToken = await loginWithFacebookForAccessToken();
                    await finishFirebaseLoginWithAccessToken(accessToken, options, addToast, router);
                } catch (err) {
                    const message = (err as Error)?.message;
                    if (message === 'facebook_login_cancelled') {
                        return;
                    }
                    if (message?.includes('Facebook App ID is not configured')) {
                        addToast('Facebook login is not configured');
                        return;
                    }
                    if (message === 'facebook_sdk_load_failed' || message === 'facebook_sdk_not_ready') {
                        // 桌面 SDK 不可用时回退整页 OAuth
                        startFacebookOAuthRedirect(returnTo, options.tracking);
                        return;
                    }
                    console.error('facebook firebase login error', err);
                    reportLoginFail({
                        login_method: options.tracking.login_method,
                        trigger_scene: options.tracking.trigger_scene,
                        fail_reason: 'facebook_sdk_login_failed',
                        fail_detail: readArmsFailDetail(err),
                    });
                    addToast('Login failed');
                } finally {
                    setIsLoading(false);
                }
            };

            runSdkLogin().catch(() => {
                // 错误已在 runSdkLogin 内处理
            });
        },
        [addToast, isLoading, router]
    );

    return { loginWithFacebook, isLoading };
}
